import { Injectable } from '@angular/core';

/**
 * Service to map product categories to their icons
 * Maps category names/IDs to PrimeNG icon classes or Font Awesome classes
 */
@Injectable({
  providedIn: 'root'
})
export class CategoryIconService {
  /**
   * Category icon mapping
   * Maps category name (case-insensitive) to icon class
   */
  private categoryIconMap: Map<string, string> = new Map([
    // Electronics & Technology
    ['electronics', 'pi pi-desktop'],
    ['technology', 'pi pi-desktop'],
    ['computer', 'pi pi-desktop'],
    ['laptop', 'pi pi-desktop'],
    ['phone', 'pi pi-mobile'],
    ['smartphone', 'pi pi-mobile'],
    ['tablet', 'pi pi-tablet'],
    
    // Clothing & Fashion
    ['clothing', 'pi pi-shopping-bag'],
    ['fashion', 'pi pi-shopping-bag'],
    ['apparel', 'pi pi-shopping-bag'],
    ['shoes', 'pi pi-circle'],
    ['footwear', 'pi pi-circle'],
    
    // Home & Kitchen
    ['home', 'pi pi-home'],
    ['kitchen', 'pi pi-home'],
    ['furniture', 'pi pi-home'],
    ['appliances', 'pi pi-cog'],
    
    // Food & Beverages
    ['food', 'pi pi-apple'],
    ['beverages', 'pi pi-apple'],
    ['groceries', 'pi pi-apple'],
    
    // Health & Beauty
    ['health', 'pi pi-heart'],
    ['beauty', 'pi pi-heart'],
    ['cosmetics', 'pi pi-heart'],
    ['pharmacy', 'pi pi-medical'],
    
    // Sports & Outdoors
    ['sports', 'pi pi-circle-fill'],
    ['outdoors', 'pi pi-circle-fill'],
    ['fitness', 'pi pi-circle-fill'],
    ['recreation', 'pi pi-circle-fill'],
    
    // Books & Media
    ['books', 'pi pi-book'],
    ['media', 'pi pi-video'],
    ['music', 'pi pi-volume-up'],
    
    // Toys & Games
    ['toys', 'pi pi-circle-on'],
    ['games', 'pi pi-circle-on'],
    ['gaming', 'pi pi-circle-on'],
    
    // Automotive
    ['automotive', 'pi pi-car'],
    ['cars', 'pi pi-car'],
    ['vehicles', 'pi pi-car'],
    
    // Pet Supplies
    ['pets', 'pi pi-circle-fill'],
    ['pet supplies', 'pi pi-circle-fill'],
    
    // Office & Business
    ['office', 'pi pi-briefcase'],
    ['business', 'pi pi-briefcase'],
    
    // Industrial & Scientific
    ['industrial', 'pi pi-cog'],
    ['scientific', 'pi pi-cog'],
    
    // Solar Energy
    ['solar', 'pi pi-sun'],
    ['energy', 'pi pi-sun'],
    
    // Garden
    ['garden', 'pi pi-leaf'],
    
    // Inverters
    ['inverters', 'pi pi-bolt'],
    
    // Default fallback
    ['default', 'pi pi-tag']
  ]);

  /**
   * Get icon class for a category
   * @param categoryName Category name
   * @returns Icon class string
   */
  getCategoryIcon(categoryName: string | null | undefined): string {
    if (!categoryName) {
      return this.categoryIconMap.get('default') || 'pi pi-tag';
    }

    // Try exact match first (case-insensitive)
    const normalized = categoryName.toLowerCase().trim();
    
    // Check for keywords in the category name
    for (const [key, icon] of this.categoryIconMap.entries()) {
      if (key !== 'default' && normalized.includes(key)) {
        return icon;
      }
    }

    // Default fallback
    return this.categoryIconMap.get('default') || 'pi pi-tag';
  }

  /**
   * Get icon class by category ID (if you want to map by ID instead)
   * @param categoryId Category ID
   * @param categoryName Category name (used as fallback)
   * @returns Icon class string
   */
  getCategoryIconById(categoryId: number, categoryName?: string): string {
    // For now, we'll use name-based mapping
    // You can extend this to include ID-based mapping if needed
    return this.getCategoryIcon(categoryName);
  }
}
